import { useEffect, useState } from "react";
import { currentSession, signOutNow, resolveName } from "./firebase";
import { dropLegacyEmployeeName } from "./storage";
import { apiScan, apiReport } from "./api";
import { describeScanError } from "./scanError";
import type { ScanResponse, BelegTyp } from "./types";
import type { CompressedImage } from "./image";
import { Analyzing } from "./screens/Analyzing";
import { Spinner } from "./ui/Spinner";
import { SignIn } from "./screens/SignIn";
import { WrongDomain } from "./screens/WrongDomain";
import { Home } from "./screens/Home";
import { Camera } from "./screens/Camera";
import { Edit } from "./screens/Edit";
import { BelegTypSelect } from "./screens/BelegTypSelect";
import { Success } from "./screens/Success";
import { Settings } from "./screens/Settings";
import { Screen, Display, Btn } from "./ui/components";
import { mono } from "./ui/tokens";

type State =
  | { kind: "boot" }
  | { kind: "signIn" }
  | { kind: "wrongDomain"; email: string }
  | { kind: "home"; employee: string }
  | { kind: "settings"; employee: string }
  | { kind: "camera"; employee: string }
  | { kind: "scanning"; employee: string; images: CompressedImage[] }
  | { kind: "selectType"; employee: string; images: CompressedImage[]; scan: ScanResponse }
  | { kind: "edit"; employee: string; images: CompressedImage[]; scan: ScanResponse; belegTyp: BelegTyp }
  | { kind: "success"; employee: string; total: number; currency: string; items: number; refId: string }
  | { kind: "error"; employee: string; message: string; detail?: string; back: "camera" | "home" };

export default function App() {
  const [state, setState] = useState<State>({ kind: "boot" });
  const [email, setEmail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        dropLegacyEmployeeName();
        const session = await currentSession();
        if (!session) { setState({ kind: "signIn" }); return; }
        // UX-only pre-check; the server is the real gate.
        if (!session.email.toLowerCase().endsWith("@bitforge.ch")) {
          setState({ kind: "wrongDomain", email: session.email });
          return;
        }
        setEmail(session.email);
        setState({ kind: "home", employee: resolveName(session) });
      } catch (err) {
        // localStorage can throw (e.g. SecurityError in a locked-down WebView)
        // and currentSession() itself is defensive but not infallible — never
        // leave the user stuck on the boot spinner with no way forward.
        console.error("[boot] failed to resolve session", err);
        setState({ kind: "signIn" });
      }
    })();
  }, []);

  async function runScan(employee: string, images: CompressedImage[]) {
    setState({ kind: "scanning", employee, images });
    try {
      const scan = await apiScan(images);
      setState({ kind: "selectType", employee, images, scan });
    } catch (e) {
      const info = describeScanError(e);
      // Logged as well as shown: the console is the only record when the
      // request never reached the server.
      console.error(`[scan] ${info.kind}: ${info.detail}`, e);
      if (info.kind === "wrongDomain") {
        setState({ kind: "wrongDomain", email });
        return;
      }
      // A rate limit is already visible server-side as a 429; the rest are the
      // ones that can otherwise vanish without trace.
      if (info.kind === "network" || info.kind === "server" || info.kind === "unknown") {
        void apiReport({
          kind: info.kind,
          detail: info.detail,
          pages: images.map((i) => ({ mimeType: i.mimeType, bytes: i.blob.size })),
        });
      }
      setState({ kind: "error", employee, message: info.message, detail: info.detail, back: "camera" });
    }
  }

  function renderScreen() {
    switch (state.kind) {
      case "boot":
        return <Spinner />;
      case "signIn":
        return <SignIn />;
      case "wrongDomain":
        return (
          <WrongDomain
            email={state.email}
            onRetry={async () => { await signOutNow(); setState({ kind: "signIn" }); }}
          />
        );
      case "home":
        return (
          <Home
            employee={state.employee}
            onStartScan={() => setState({ kind: "camera", employee: state.employee })}
            onOpenSettings={() => setState({ kind: "settings", employee: state.employee })}
          />
        );
      case "settings":
        return (
          <Settings
            employee={state.employee}
            email={email}
            onBack={() => setState({ kind: "home", employee: state.employee })}
            onSignedOut={() => setState({ kind: "signIn" })}
            onNameChange={(name) =>
              setState((s) => (s.kind === "settings" ? { kind: "settings", employee: name } : s))
            }
          />
        );
      case "camera":
        return (
          <Camera
            onScan={(imgs) => runScan(state.employee, imgs)}
            onCancel={() => setState({ kind: "home", employee: state.employee })}
          />
        );
      case "scanning":
        return <Analyzing />;
      case "selectType":
        return (
          <BelegTypSelect
            onSelect={(belegTyp) =>
              setState({
                kind: "edit",
                employee: state.employee,
                images: state.images,
                scan: state.scan,
                belegTyp,
              })
            }
            onBack={() => setState({ kind: "camera", employee: state.employee })}
          />
        );
      case "edit":
        return (
          <Edit
            employee={state.employee}
            email={email}
            images={state.images}
            initial={state.scan}
            belegTyp={state.belegTyp}
            onSuccess={(total, currency, items, refId) =>
              setState({ kind: "success", employee: state.employee, total, currency, items, refId })
            }
            onWrongDomain={(email) => setState({ kind: "wrongDomain", email })}
            onScrap={() => setState({ kind: "camera", employee: state.employee })}
            onBack={() =>
              setState({
                kind: "selectType",
                employee: state.employee,
                images: state.images,
                scan: state.scan,
              })
            }
          />
        );
      case "success":
        return (
          <Success
            total={state.total}
            currency={state.currency}
            items={state.items}
            refId={state.refId}
            onAnother={() => setState({ kind: "camera", employee: state.employee })}
            onHome={() => setState({ kind: "home", employee: state.employee })}
          />
        );
      case "error":
        return (
          <Screen padTop={80}>
            <Display>Fehler</Display>
            <p style={{ color: "var(--fg-2)", marginTop: 16 }}>{state.message}</p>
            {state.detail && (
              <p style={{
                marginTop: 12, padding: "8px 12px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                fontFamily: mono, fontSize: 11, lineHeight: 1.5,
                color: "var(--fg-3)", wordBreak: "break-word",
              }}>
                {state.detail}
              </p>
            )}
            <div style={{ marginTop: 24 }}>
              <Btn
                onClick={() =>
                  state.back === "camera"
                    ? setState({ kind: "camera", employee: state.employee })
                    : setState({ kind: "home", employee: state.employee })
                }
              >
                Zurück
              </Btn>
            </div>
          </Screen>
        );
    }
  }

  return (
    <div key={state.kind} className="screen-enter">
      {renderScreen()}
    </div>
  );
}
